import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';
import { pageSEO } from '../utils/seoConfig';
import Header from './Header';
import Footer from './Footer';
import Solutions from './Solutions';
import SEOHead from './SEOHead';
import { Zap, Shield, Users, BarChart } from 'lucide-react';

const SolutionsPage = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const seo = pageSEO.solutions;

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={seo.title[language]}
        description={seo.description[language]}
        keywords={seo.keywords[language]}
        url="/solutions"
      />
      <Header />

      <main>
        <section className="pt-24 pb-16 bg-gradient-to-br from-blue-50 via-white to-gray-50" aria-labelledby="solutions-heading">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium mb-6" style={{ backgroundColor: '#e0f2fe', color: '#115d7b' }} role="status" aria-label={language === 'en' ? 'Integrated Business Solutions' : 'Soluciones Empresariales Integradas'}>
              <Zap className="w-4 h-4 mr-2" aria-hidden="true" />
              {language === 'en' ? 'Integrated Business Solutions' : 'Soluciones Empresariales Integradas'}
            </div>
            <h1 id="solutions-heading" className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              {language === 'en' ? 'Transform Your Business Operations' : 'Transforma las Operaciones de Tu Negocio'}
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              {language === 'en' 
                ? 'Comprehensive suite of integrated modules designed to streamline operations, boost productivity, and drive growth across your entire organization.'
                : 'Suite integral de módulos integrados diseñados para optimizar operaciones, aumentar productividad e impulsar el crecimiento en toda tu organización.'
              }
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/demo"
                className="px-8 py-4 text-white rounded-lg transition-colors font-bold text-lg shadow-lg"
                style={{ backgroundColor: '#115d7b' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#0f5269'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#115d7b'}
              >
                {language === 'en' ? 'Explore Solutions' : 'Explorar Soluciones'}
              </a>
              <a
                href="/success-cases"
                className="px-8 py-4 border-2 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-bold text-lg"
                style={{ borderColor: '#115d7b' }}
              >
                {language === 'en' ? 'View Success Stories' : 'Ver Casos de Éxito'}
              </a>
            </div>
          </div>

          {/* Key Benefits Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto" role="list" aria-label={language === 'en' ? 'Key Benefits' : 'Beneficios Clave'}>
            <article className="bg-white p-8 rounded-xl shadow-lg text-center" role="listitem">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4" aria-hidden="true">
                <Shield className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                {language === 'en' ? 'Enterprise Security' : 'Seguridad Empresarial'}
              </h2>
              <p className="text-gray-600">
                {language === 'en' ? 'Bank-level security with 99.9% uptime guarantee' : 'Seguridad nivel bancario con garantía de 99.9% de tiempo activo'}
              </p>
            </article>
            <article className="bg-white p-8 rounded-xl shadow-lg text-center" role="listitem">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" aria-hidden="true">
                <Users className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                {language === 'en' ? 'Seamless Integration' : 'Integración Perfecta'}
              </h2>
              <p className="text-gray-600">
                {language === 'en' ? 'All modules work together in perfect harmony' : 'Todos los módulos trabajan juntos en perfecta armonía'}
              </p>
            </article>
            <article className="bg-white p-8 rounded-xl shadow-lg text-center" role="listitem">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4" aria-hidden="true">
                <BarChart className="w-8 h-8 text-purple-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                {language === 'en' ? 'Real-time Analytics' : 'Análisis en Tiempo Real'}
              </h2>
              <p className="text-gray-600">
                {language === 'en' ? 'Make data-driven decisions with live insights' : 'Toma decisiones basadas en datos con insights en vivo'}
              </p>
            </article>
          </div>
        </div>
        </section>

        <Solutions />
      </main>
      <Footer />
    </div>
  );
};

export default SolutionsPage;